import type { MatchDetail } from "../../contracts/utils/metadata/MatchDetail";
import type { MatchDetailDifferenceKey } from "../../contracts/utils/metadata/MatchDetailDifferenceKey";
import type { MetadataPropertyInfo } from "../../contracts/utils/metadata/MetadataPropertyInfo";
import type { MetadataTypeInfo as MetadataTypeInfoContract } from "../../contracts/utils/metadata/MetadataTypeInfo";

//
export class MetadataTypeInfo<T> implements MetadataTypeInfoContract<T> {
	private readonly props: Map<string, MetadataPropertyInfo<T>>;
	constructor(private readonly properties: MetadataPropertyInfo<T>[]) {
		this.props = new Map<string, MetadataPropertyInfo<T>>(
			this.properties.map((v) => [String(v.getName()), v]),
		);
	}

	matchType(
		value: unknown,
		options?: { strict: boolean },
	): value is MetadataTypeInfoContract<T> {
		const match = this.matchTypeDetail(value);
		if (options?.strict) {
			return match.matchStrict;
		}
		return match.match;
	}

	matchTypeDetail(value: unknown): MatchDetail {
		// const defkeys = this.properties.map((v) => `${String(v.getName())}`);

		if (value == null || typeof value !== "object") {
			return {
				match: false,
				matchStrict: false,
				extraKeys: [],
				missingKeys: Array.from(this.props.keys()),
				differenceKeys: [],
			};
		}

		const extra_keys = new Set<string>();
		const missing_keys = new Set<string>();
		const difference_keys: MatchDetailDifferenceKey[] = [];

		const all_keys = new Set([
			...(this.props.keys() as keyof object),
			...(Object.keys(value) as keyof object),
		]);

		for (const datakey of all_keys) {
			const prop = this.props.get(datakey);
			if (!prop) {
				extra_keys.add(datakey);
				continue;
			}
			if (!(datakey in value)) {
				if (!prop.isOptional()) {
					missing_keys.add(datakey);
				}
				continue;
			}
			const val = value[datakey];
			if (prop.matchProperty(val)) {
				continue;
			}
			difference_keys.push({
				key: datakey,
				expected: `${prop}`,
				actual: val,
			});
		}

		return {
			match: missing_keys.size === 0 && difference_keys.length === 0,
			matchStrict:
				missing_keys.size === 0 &&
				extra_keys.size === 0 &&
				difference_keys.length === 0,
			extraKeys: Array.from(extra_keys),
			missingKeys: Array.from(missing_keys),
			differenceKeys: difference_keys,
		};
	}

	getPropertyInfoByName(
		prop_name: string | keyof T,
	): MetadataPropertyInfo<T> | undefined {
		for (const element of this.properties) {
			if (`${String(element.getName())}` === `${String(prop_name)}`) {
				return element;
			}
		}
		return undefined;
	}
}
